import cover2Json from '@tdmons/data/plays/def-cover-2-zone.json';
import cover3Json from '@tdmons/data/plays/def-cover-3.json';
import manPressJson from '@tdmons/data/plays/def-man-press.json';
import runStuffJson from '@tdmons/data/plays/def-run-stuff.json';
import zoneBlitzJson from '@tdmons/data/plays/def-zone-blitz.json';
import fourVerticalsJson from '@tdmons/data/plays/off-shotgun-four-verticals.json';
import screenJson from '@tdmons/data/plays/off-shotgun-rb-screen.json';
import quickSlantsJson from '@tdmons/data/plays/off-shotgun-trips-right-quick-slants.json';
import insideZoneJson from '@tdmons/data/plays/off-singleback-inside-zone.json';
import outsideZoneJson from '@tdmons/data/plays/off-singleback-outside-zone.json';
import paDeepPostJson from '@tdmons/data/plays/off-singleback-pa-deep-post.json';
import { parseDefensePlay, parseOffensePlay } from './playdef';
import type { DefensePlayDef, OffensePlayDef } from './types';

/** The plays a game may call. The first play on each side is the default when a coach calls none. */
export interface Playbook {
  offense: readonly OffensePlayDef[];
  defense: readonly DefensePlayDef[];
}

const quickSlants = parseOffensePlay(quickSlantsJson);
const cover2 = parseDefensePlay(cover2Json);

/**
 * The playbook floor (slice 6b): six offense plays and five defense plays, each tagged with its
 * situations, what it leans on, and its risk. See docs/design-plan.md, "The playbook floor".
 */
export const PLAYBOOK_FLOOR: Playbook = {
  offense: [
    quickSlants,
    parseOffensePlay(insideZoneJson),
    parseOffensePlay(outsideZoneJson),
    parseOffensePlay(paDeepPostJson),
    parseOffensePlay(screenJson),
    parseOffensePlay(fourVerticalsJson),
  ],
  defense: [
    cover2,
    parseDefensePlay(cover3Json),
    parseDefensePlay(manPressJson),
    parseDefensePlay(zoneBlitzJson),
    parseDefensePlay(runStuffJson),
  ],
};

/**
 * The v1 playbook as authored in packages/data/plays: the floor until slice 16 grows it to 40.
 * Quick Slants and Cover 2 stay first, the defaults when a coach calls none.
 */
export const DEFAULT_PLAYBOOK: Playbook = PLAYBOOK_FLOOR;

/** A play by id, or the side's default when `id` is undefined. Throws on an id not in the book. */
export function playById<P extends OffensePlayDef | DefensePlayDef>(
  plays: readonly P[],
  id: string | undefined,
): P {
  const play = id === undefined ? plays[0] : plays.find((p) => p.id === id);
  if (!play)
    throw new Error(id === undefined ? 'the playbook is empty' : `no play "${id}" in the playbook`);
  return play;
}
